import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy, tickSizeToPrecision } from '../bootstrap';
import { aggregateOrderbook } from '../utils';

const BTSE_REST_BASE = 'https://api.btse.com/public-api/market/v1';
const BTSE_PROXY_PATH = '/api/v1/proxy';
const BTSE_REST_POLL_INTERVAL_MS = 5_000;
const BTSE_MAX_BOOK_DEPTH = 50;
const BTSE_MAX_KLINES = 300;
const BTSE_MAX_TRADES = 100;
const BTSE_LIVE_TRADES = 20;
const BTSE_SEEN_TRADES_LIMIT = 500;

// BTSE kline resolution is a number of minutes.
const BTSE_RESOLUTION_BY_TIMEFRAME: Partial<Record<IChartTimeframe, string>> = {
  '1s': '1',
  '10s': '1',
  '30s': '1',
  '1m': '1',
  '3m': '5',
  '5m': '5',
  '15m': '15',
  '30m': '30',
  '1h': '60',
  '2h': '60',
  '4h': '240',
  '6h': '360',
  '8h': '360',
  '12h': '360',
  '24h': '1440',
  '1d': '1440',
  '2d': '1440',
  '3d': '1440',
  '5d': '1440',
  '168h': '10080',
  '1w': '10080',
  '1M': '43200',
};

interface BtseEnvelope<T> {
  success?: boolean;
  code: number;
  msg?: string;
  data: T;
}

interface BtseSymbol {
  symbol: string;
  active: boolean;
  minPriceIncrement: string | number;
  pricePrecision?: number;
}

export interface BtseDayTicker {
  symbol: string;
  lastPrice: string | number;
  openPrice: string | number;
  highPrice: string | number;
  lowPrice: string | number;
  amount: string | number;
  volume: string | number;
  priceChangePercent?: string | number;
  bidPrice?: string | number;
  askPrice?: string | number;
}

interface BtseTrade {
  id: string | number;
  timestamp: string | number;
  price: string | number;
  size: string | number;
  side: string;
}

type BtseRawKline = (string | number)[];

/** BTSE Markets v1 answers `code: 1` on success; any other code is an error. */
export const btseProxyGet = async <T>(
  path: string,
  params: Record<string, string | number> = {},
): Promise<T> => {
  const target = new URL(`${BTSE_REST_BASE}${path}`);
  Object.entries(params).forEach(([key, value]) => target.searchParams.set(key, String(value)));
  const response = await proxyAxios.get<BtseEnvelope<T>>(BTSE_PROXY_PATH, {
    params: { url: target.toString() },
  });
  const envelope = response.data;
  if (envelope?.code !== 1) {
    throw new Error(`BTSE API ${envelope?.code}: ${envelope?.msg ?? 'request failed'}`);
  }
  return envelope.data;
};

const toMilliseconds = (value: string | number): number => {
  const timestamp = Number(value);
  return timestamp < 10_000_000_000 ? timestamp * 1_000 : timestamp;
};

const toSeconds = (value: number): number =>
  Math.floor(value < 10_000_000_000 ? value : value / 1_000);

/**
 * BTSE row: [time (s), open, high, low, close, quote volume], newest first.
 * The dashboard needs base volume, so it is derived from the close price.
 */
export const normalizeBtseKlines = (rows: BtseRawKline[]): KLine[] =>
  rows
    .filter(
      row => row.length >= 6 && row.slice(0, 6).every(value => Number.isFinite(Number(value))),
    )
    .map(row => {
      const close = Number(row[4]);
      const baseVolume = close > 0 ? Number(row[5]) / close : 0;
      return [
        toMilliseconds(row[0]),
        String(row[1]),
        String(row[2]),
        String(row[3]),
        String(row[4]),
        normalizeNumber(Number(baseVolume.toFixed(8))),
      ] as KLine;
    })
    .sort((left, right) => left[0] - right[0]);

export const normalizeBtseTrades = (rows: BtseTrade[]): TradeSocket[] =>
  rows
    .filter(row => {
      const side = String(row.side).toLowerCase();
      return (
        Number(row.timestamp) > 0 &&
        Number(row.price) > 0 &&
        Number(row.size) > 0 &&
        (side === 'buy' || side === 'sell')
      );
    })
    .map(row => ({
      price: String(row.price),
      quantity: String(row.size),
      timestamp: String(toMilliseconds(row.timestamp)),
      side: String(row.side).toLowerCase(),
    }));

/** 24h change in percent. The API field is a fraction with 3 decimals, so prices are preferred. */
export const btseChangePercent = (ticker: BtseDayTicker): string => {
  const last = Number(ticker.lastPrice);
  const open = Number(ticker.openPrice);
  if (Number.isFinite(last) && Number.isFinite(open) && open > 0) {
    return normalizeNumber(Number((((last - open) / open) * 100).toFixed(2)));
  }
  const fraction = Number(ticker.priceChangePercent);
  return Number.isFinite(fraction) ? normalizeNumber(Number((fraction * 100).toFixed(2))) : '0';
};

export class BtseStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pollInterval: ReturnType<typeof setInterval> | undefined;
  pair: string;
  base: string;
  quote: string;
  depth: number;
  precision: number;
  selectedPrecision: number;
  activeIntervalMs: number;
  seenTradeIds: string[];
  tradesSeeded: boolean;
  asks: [string, string][];
  bids: [string, string][];

  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.base = base.toUpperCase();
    this.quote = quote.toUpperCase();
    this.pair = `${this.base}-${this.quote}`;
    this.depth = Math.min(orderbookDepth ?? 20, BTSE_MAX_BOOK_DEPTH);
    this.precision = 1;
    this.selectedPrecision = 1;
    this.activeIntervalMs = 60_000;
    this.seenTradeIds = [];
    this.tradesSeeded = false;
    this.asks = [];
    this.bids = [];
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const response = await btseProxyGet<{ symbols: BtseSymbol[] }>('/exchangeInfo', {
        symbol: this.pair,
      });
      const metadata = response.symbols?.find(
        item => item.symbol.toUpperCase() === this.pair && item.active,
      );
      const precision = metadata
        ? (tickSizeToPrecision(metadata.minPriceIncrement) ?? metadata.pricePrecision)
        : undefined;
      if (precision === undefined || !Number.isInteger(precision) || precision < 0) {
        throw new Error(`BTSE symbol metadata is missing for ${this.pair}`);
      }
      this.precision = precision;
      this.selectedPrecision = precision;
      this.precisionUpdate(precision);
    } catch (error) {
      console.log('BTSE precision error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await btseProxyGet<{
        asks: (string | number)[][];
        bids: (string | number)[][];
      }>('/orderbook', { symbol: this.pair, depth: BTSE_MAX_BOOK_DEPTH });
      const normalize = (rows: (string | number)[][]): [string, string][] =>
        (rows ?? [])
          .filter(row => row.length >= 2 && Number(row[0]) > 0 && Number(row[1]) > 0)
          .map(row => [String(row[0]), String(row[1])]);
      // BTSE returns asks from the highest price down, so both sides are sorted here.
      this.asks = normalize(response.asks).sort(
        (left, right) => Number(left[0]) - Number(right[0]),
      );
      this.bids = normalize(response.bids).sort(
        (left, right) => Number(right[0]) - Number(left[0]),
      );
      this.publishOrderbook();
    } catch (error) {
      console.log('BTSE orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const ticker = await btseProxyGet<BtseDayTicker>('/ticker/24hr', { symbol: this.pair });
      if (!ticker || !(Number(ticker.lastPrice) > 0)) {
        throw new Error(`BTSE ticker is missing for ${this.pair}`);
      }
      this.onTickerUpdate({
        baseVolume: String(ticker.amount),
        change: btseChangePercent(ticker),
        close: String(ticker.lastPrice),
        high: String(ticker.highPrice),
        last: String(ticker.lastPrice),
        low: String(ticker.lowPrice),
        quoteVolume: String(ticker.volume),
      });
    } catch (error) {
      console.log('BTSE ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    try {
      const count = Math.min(Math.max(limit, 1), BTSE_MAX_KLINES);
      const rows = await btseProxyGet<BtseRawKline[]>('/klines', {
        symbol: this.pair,
        resolution: BTSE_RESOLUTION_BY_TIMEFRAME[interval] ?? '1',
        end: toSeconds(to),
        limit: count,
      });
      const fromMs = toMilliseconds(from);
      return normalizeBtseKlines(rows ?? [])
        .filter(row => row[0] >= fromMs)
        .slice(-count);
    } catch (error) {
      console.log('BTSE klines error:', error);
      return [];
    }
  };

  fetchRawTrades = async (limit: number): Promise<BtseTrade[]> => {
    const rows = await btseProxyGet<BtseTrade[]>('/trades', {
      symbol: this.pair,
      limit: Math.min(Math.max(limit, 1), BTSE_MAX_TRADES),
    });
    return rows ?? [];
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    try {
      return normalizeBtseTrades(await this.fetchRawTrades(limit));
    } catch (error) {
      console.log('BTSE trades error:', error);
      return [];
    }
  };

  /** Emits only trades that were not seen yet. The first poll only seeds the id set. */
  pollTrades = async () => {
    try {
      const rows = await this.fetchRawTrades(BTSE_LIVE_TRADES);
      const fresh = rows.filter(row => !this.seenTradeIds.includes(String(row.id)));
      this.seenTradeIds = [...this.seenTradeIds, ...fresh.map(row => String(row.id))].slice(
        -BTSE_SEEN_TRADES_LIMIT,
      );
      if (!this.tradesSeeded) {
        this.tradesSeeded = true;
        return;
      }
      normalizeBtseTrades(fresh)
        .sort((left, right) => Number(left.timestamp) - Number(right.timestamp))
        .forEach(trade => this.onTradeUpdate(trade));
    } catch (error) {
      console.log('BTSE live trades error:', error);
    }
  };

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(this.asks, this.bids, this.selectedPrecision, this.depth),
    );
  };

  pollLive = async () => {
    const now = Date.now();
    const [, , , klines] = await Promise.all([
      this.fetchOrderbook(),
      this.fetchTicker(),
      this.pollTrades(),
      this.fetchKlines({
        base: this.base,
        quote: this.quote,
        from: now - this.activeIntervalMs * 2,
        to: now + 1_000,
        limit: 2,
        interval: this.intervalToTimeframe(),
      }),
    ]);
    const kline = klines.at(-1);
    if (kline) this.klineUpdate(kline);
  };

  intervalToTimeframe = (): IChartTimeframe => {
    const mapping: Record<number, IChartTimeframe> = {
      60_000: '1m',
      300_000: '5m',
      900_000: '15m',
      1_800_000: '30m',
      3_600_000: '1h',
      14_400_000: '4h',
      21_600_000: '6h',
      86_400_000: '1d',
      604_800_000: '1w',
    };
    return mapping[this.activeIntervalMs] ?? '1m';
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.activeIntervalMs = interval;
    void this.pollLive();
    this.pollInterval = setInterval(() => void this.pollLive(), BTSE_REST_POLL_INTERVAL_MS);
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.depth = Math.min(Math.max(depth, 1), BTSE_MAX_BOOK_DEPTH);
    void this.fetchOrderbook();
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: Math.max(prec, 1) })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(value => ({ value: normalizeNumber(1 / 10 ** value), tick: value, id: value }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await Promise.all([this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const instance = new BtseStrategy(args);
    await bootstrapStrategy(instance);
    return instance;
  };
}
